import React from "react";
import { fileUrl } from "../lib/api";
import { safeEmbedUrl, safeUrl } from "../utils/security";
import RichTextRenderer from "./RichTextRenderer";
import OfflineImage from "./OfflineImage";

/**
 * BlockRenderer renders a list of content blocks (text, heading, image, video,
 * audio, youtube, vimeo, file, callout, divider, quote).
 */

export function BlockView({ block }) {
  const c = block.content || {};

  switch (block.type) {
    case "heading": {
      const level = Math.min(Math.max(c.level || 2, 1), 6);
      const Tag = `h${level}`;
      return (
        <Tag
          className="font-heading text-brand-green font-bold mt-8 mb-3"
          style={{ fontSize: `${2.4 - Math.min(level, 4) * 0.2}rem` }}
        >
          {c.text}
        </Tag>
      );
    }

    case "text":
      return <RichTextRenderer html={c.text} className="text-base leading-relaxed text-black/85 my-3" />;

    case "image":
      return (
        <figure className="my-6">
          <OfflineImage src={safeUrl(fileUrl(c.url))} alt={c.caption || ""} className="rounded-xl w-full" loading="lazy" />
          {c.caption && <figcaption className="text-center text-sm text-gray-500 mt-2">{c.caption}</figcaption>}
        </figure>
      );

    case "video":
      return (
        <div className="my-6">
          <video src={safeUrl(fileUrl(c.url))} controls className="w-full rounded-xl bg-black" />
          {c.caption && <p className="text-center text-sm text-gray-500 mt-2">{c.caption}</p>}
        </div>
      );

    case "audio":
      return (
        <div className="my-6 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
          {(c.caption || c.name) && (
            <p className="font-heading font-bold text-lg mb-3 text-black">
              {c.caption || c.name}
            </p>
          )}
          <audio src={safeUrl(fileUrl(c.url))} controls className="w-full" preload="metadata" />
        </div>
      );

    case "youtube":
    case "vimeo": {
      const src = safeEmbedUrl(c.url);
      if (!src) return null;

      return (
        <div className="my-6">
          <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl bg-black">
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={src}
              title={c.caption || "Vidéo"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
          {c.caption && <p className="text-center text-sm text-gray-500 mt-2">{c.caption}</p>}
        </div>
      );
    }

    case "file":
      return (
        <a
          href={safeUrl(fileUrl(c.url))}
          target="_blank"
          rel="noopener noreferrer"
          className="my-4 inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
        >
          📎 <span className="font-medium">{c.name || "Télécharger le fichier"}</span>
        </a>
      );

    case "quote":
      return (
        <blockquote className="my-6 border-l-4 border-brand-green pl-4 italic text-gray-700">
          <RichTextRenderer html={c.text} />
          {c.author && <footer className="text-sm mt-2 not-italic">— {c.author}</footer>}
        </blockquote>
      );

    case "callout":
      return (
        <div className={`my-6 p-4 rounded-xl border-l-4 ${c.variant === "warning" ? "border-amber-500 bg-amber-50" : "border-brand-green bg-emerald-50"}`}>
          {c.title && <p className="font-semibold mb-1">{c.title}</p>}
          <RichTextRenderer html={c.text} />
        </div>
      );

    case "divider":
      return <hr className="my-8 border-emerald-100" />;

    default:
      return <div className="my-2 text-xs text-gray-400">Bloc inconnu: {block.type}</div>;
  }
}

export default function BlockRenderer({ blocks = [] }) {
  return (
    <div className="prose-sf max-w-none">
      {blocks.map((b) => <BlockView key={b.id} block={b} />)}
    </div>
  );
}
